const isPlausibleAvailable = () => {
    return typeof window !== 'undefined' && window.plausible;
};

export const trackEvent = (eventName, properties = {}) => {
    if (!isPlausibleAvailable()) {
        console.warn('Plausible not available, skipping tracking:', eventName);
        return;
    }

    try {
        window.plausible(eventName, {
            props: {
                timestamp: new Date().toISOString(),
                ...properties,
            },
        });
    } catch (error) {
        console.error('Error tracking event:', eventName, error);
    }
};

export const trackPageView = (page, properties = {}) => {
    trackEvent('Page View', {
        page,
        ...properties,
    });
};

export const trackAuth = {
    login: (method = 'email') => trackEvent('User Login', { method }),
    register: (source = 'organic') => trackEvent('User Register', { source }),
    logout: () => trackEvent('User Logout'),
    passwordReset: (step = 'request') => trackEvent('Password Reset', { step }),
    passwordChange: () => trackEvent('Password Change'),
};

export const trackForm = {
    start: (formName, step = 1) => trackEvent('Form Start', { form: formName, step }),
    complete: (formName, timeSpent = null) =>
        trackEvent('Form Complete', {
            form: formName,
            time_spent: timeSpent,
        }),
    error: (formName, errorType, field = null) =>
        trackEvent('Form Error', {
            form: formName,
            error_type: errorType,
            field,
        }),
    abandon: (formName, step = null) =>
        trackEvent('Form Abandon', {
            form: formName,
            step,
        }),
};

export const trackCTA = {
    click: (buttonName, page, position = null) =>
        trackEvent('CTA Click', {
            button: buttonName,
            page,
            position,
        }),
    primaryAction: (action, page) => trackEvent('Primary Action', { action, page }),
    secondaryAction: (action, page) => trackEvent('Secondary Action', { action, page }),
};

export const trackDashboard = {
    featureUsed: (feature, section = null) =>
        trackEvent('Feature Used', {
            feature,
            section,
        }),
    filterApplied: (filterType, page) =>
        trackEvent('Filter Applied', {
            filter_type: filterType,
            page,
        }),
    dataExport: (dataType, format = 'csv') =>
        trackEvent('Data Export', {
            data_type: dataType,
            format,
        }),
    searchUsed: (searchTerm, page, resultsCount = null) =>
        trackEvent('Search Used', {
            search_term: searchTerm,
            page,
            results_count: resultsCount,
        }),
};

export const trackBusiness = {
    vehicleAdded: (vehicleType = 'taxi') =>
        trackEvent('Vehicle Added', { vehicle_type: vehicleType }),
    employeeAdded: (role = 'driver') => trackEvent('Employee Added', { role }),
    shiftScheduled: (duration = null) => trackEvent('Shift Scheduled', { duration }),
    paymentProcessed: (method, amountRange = null) =>
        trackEvent('Payment Processed', {
            method,
            amount_range: amountRange,
        }),
};

export const trackContent = {
    blogPostView: (postSlug, category = null) =>
        trackEvent('Blog Post View', {
            post_slug: postSlug,
            category,
        }),
    blogSearch: (query, resultsCount) =>
        trackEvent('Blog Search', {
            query,
            results_count: resultsCount,
        }),
    contentDownload: (contentType, contentName) =>
        trackEvent('Content Download', {
            content_type: contentType,
            content_name: contentName,
        }),
};

export const trackNavigation = {
    routeChange: (from, to, authenticated = false) =>
        trackEvent('Route Change', {
            from_page: from,
            to_page: to,
            user_authenticated: authenticated,
        }),
    externalLinkClick: (url, linkText = null) =>
        trackEvent('External Link Click', {
            url,
            link_text: linkText,
        }),
    mobileAppRedirect: (source = 'banner') => trackEvent('Mobile App Redirect', { source }),
};

export const trackError = {
    jsError: (errorMessage, component = null) =>
        trackEvent('JavaScript Error', {
            error_message: errorMessage,
            component,
        }),
    apiError: (endpoint, errorCode) =>
        trackEvent('API Error', {
            endpoint,
            error_code: errorCode,
        }),
    userError: (errorType, context) =>
        trackEvent('User Error', {
            error_type: errorType,
            context,
        }),
};

export const trackEngagement = {
    scrollDepth: (percentage, page) =>
        trackEvent('Scroll Depth', {
            percentage,
            page,
        }),
    timeOnPage: (duration, page) =>
        trackEvent('Time on Page', {
            duration,
            page,
        }),
    sessionDuration: (duration, pageViews) =>
        trackEvent('Session Duration', {
            duration,
            page_views: pageViews,
        }),
};

export const trackPWA = {
    promptShown: (platform, trigger = 'auto') =>
        trackEvent('PWA Prompt Shown', {
            platform,
            trigger,
        }),
    installClicked: (platform) =>
        trackEvent('PWA Install Clicked', {
            platform,
        }),
    installAccepted: (platform) =>
        trackEvent('PWA Install Accepted', {
            platform,
        }),
    installDeclined: (platform) =>
        trackEvent('PWA Install Declined', {
            platform,
        }),
    installDismissed: (platform, action) =>
        trackEvent('PWA Prompt Dismissed', {
            platform,
            action,
        }),
    installError: (platform, error) =>
        trackEvent('PWA Install Error', {
            platform,
            error,
        }),
};

export const initializeTracking = (userProperties = {}) => {
    if (!isPlausibleAvailable()) {
        return;
    }

    trackEvent('App Initialized', {
        user_agent: navigator.userAgent,
        screen_resolution: `${screen.width}x${screen.height}`,
        ...userProperties,
    });
};

export default {
    trackEvent,
    trackPageView,
    trackAuth,
    trackForm,
    trackCTA,
    trackDashboard,
    trackBusiness,
    trackContent,
    trackNavigation,
    trackError,
    trackEngagement,
    trackPWA,
    initializeTracking,
    isPlausibleAvailable,
};
